import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { useWorkOrderNew } from './hooks/useWorkOrderNew';
import { NewBasicInfo } from './components/NewBasicInfo';
import { NewAssignment } from './components/NewAssignment';
import { NewAdditionalInfo } from './components/NewAdditionalInfo';
import { NewSidebarSettings } from './components/NewSidebarSettings';
import type { WorkOrderNewFormValues } from './types/workOrderNew.types';
import { PageHeaderNew } from '@/components/ui';

const today = new Date().toISOString().slice(0, 10);

const initialFormState: WorkOrderNewFormValues = {
  title: '',
  work_order_code: '',
  project_id: '',
  zone_id: '',
  phase_id: '',
  boq_item_id: '',
  description: '',
  lead_user_id: '',
  collaborator_ids: [],
  assigned_team_id: '',
  planned_start_date: today,
  planned_start_time: '07:30',
  planned_end_date: today,
  planned_end_time: '17:00',
  estimated_hours: '',
  priority_code: 'medium',
  work_type: 'construction',
  planned_quantity: '0',
  unit: '',
  notes: '',
  category_id: '',
  sub_category_id: '',
  // Maintenance specific fields
  area_m2: '',
  tree_count: '',
  lawn_area_m2: '',
  shrub_area_m2: '',
  hedge_length_m: '',
  material_quantity: '',
};

export default function WorkOrderNewPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const [form, setForm] = useState<WorkOrderNewFormValues>(initialFormState);
  const { referenceData, isLoadingOptions, createWorkOrder, isSaving } = useWorkOrderNew(companyId);

  function updateField(field: string, value: any) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!profile?.id) return;
    if (!form.title.trim()) { toast.error('Vui lòng nhập tên công việc'); return; }
    if (!form.project_id) { toast.error('Vui lòng chọn dự án'); return; }

    try {
      const result = await createWorkOrder({ userId: profile.id, values: form });
      toast.success('Đã tạo lệnh thi công');
      navigate(`/work-orders/detail/${result.id}`);
    } catch (err: any) {
      toast.error(err.message || 'Lỗi khi tạo lệnh thi công');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4 bg-slate-50 min-h-screen text-slate-900">
      {/* Breadcrumb / Header */}
      <PageHeaderNew 
        title="Tạo công việc"
        subtitle="Nhập thông tin để tạo công việc mới."
        backUrl="/work-orders"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Main Form Area */}
        <div className="lg:col-span-9 space-y-4">
          <NewBasicInfo
            form={form}
            projects={referenceData?.projects || []}
            referenceData={referenceData}
            onChange={updateField}
          />
          <NewAssignment
            form={form}
            users={referenceData?.users || []}
            teams={referenceData?.teams || []}
            onChange={updateField}
          />
          <NewAdditionalInfo
            form={form}
            onChange={updateField}
          />
        </div>

        {/* Sidebar Area */}
        <div className="lg:col-span-3 space-y-4">
          <NewSidebarSettings />
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-100">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-10 px-8 rounded-md border border-slate-200 bg-white sm:text-sm text-xs text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Hủy bỏ
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="h-10 px-8 flex items-center gap-2 rounded-md bg-emerald-800 sm:text-sm text-xs text-white hover:bg-emerald-900 transition-all -emerald-950/20 disabled:opacity-50"
        >
          {isSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-add-box-line" />}
          {isSaving ? 'Đang tạo...' : 'Tạo công việc'}
        </button>
      </div>
    </form>
  );
}
